import { IMAGES } from "../lib/site";
import PageHero from "../components/site/PageHero";
import CTASection from "../components/site/CTASection";
import { Warehouse, Home as HomeIcon, Trash2 } from "lucide-react";

const AREAS = [
  {
    icon: Warehouse,
    title: "Garage & Shed Clearouts",
    copy: "Years of clutter, boxes and old tools cleared, sorted and disposed of properly — a usable space again by the end of the day.",
  },
  {
    icon: HomeIcon,
    title: "House Clearouts",
    copy: "Sensitive, respectful full-property clearances — for probate, downsizing, or end-of-tenancy. Discreet and thorough.",
  },
  {
    icon: Trash2,
    title: "Waste Removal",
    copy: "Single loads or large jobs — we haul, we sort, we recycle where possible, and we take a clean site with us.",
  },
];

export default function Clearance() {
  return (
    <>
      <PageHero
        overline="Clear space, clear mind"
        title="Clearance & Removal"
        subtitle="Garage, shed and full-house clearances plus responsible waste removal across Norfolk."
        image={IMAGES.clearance}
        testId="clearance-hero"
      />

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="overline text-neutral-500">The service</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Handled with care. Cleared with respect. Removed responsibly.
            </h2>
            <p className="mt-8 text-neutral-600 text-lg leading-relaxed max-w-2xl">
              Clearance work often carries a lot more than physical weight —
              lifetimes, changes, awkward timing. We treat every job with
              patience, discretion and proper disposal.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {AREAS.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                className="bg-white p-10"
                data-testid={`cl-area-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <Icon size={28} strokeWidth={1.5} />
                <h3 className="mt-6 font-serif-display text-2xl leading-tight">
                  {a.title}
                </h3>
                <p className="mt-4 text-neutral-600 leading-relaxed">
                  {a.copy}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Process strip */}
      <section
        className="bg-neutral-950 text-white"
        data-testid="clearance-process"
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 fade-up">
          <p className="overline text-neutral-400 mb-6">How it works</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-14 max-w-3xl">
            A simple, honest four-step process.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            {[
              { n: "01", t: "Walkthrough", d: "We view the space, agree scope and quote transparently." },
              { n: "02", t: "Schedule", d: "We book a day that suits you — evenings and weekends welcome." },
              { n: "03", t: "Clear & Sort", d: "We remove, categorise and separate for recycling & reuse." },
              { n: "04", t: "Hand Back", d: "A swept-clean space and every last piece properly disposed of." },
            ].map((s) => (
              <div key={s.n} className="border-t border-white/20 pt-6">
                <p className="font-serif-display text-4xl">{s.n}</p>
                <p className="mt-4 text-lg font-medium">{s.t}</p>
                <p className="mt-2 text-neutral-400 text-sm leading-relaxed">
                  {s.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
